import type { Building } from "building-types/building/buildingTypes";
import { DatumVerticalUsageType } from "building-types/datums/datumUsageTypes";
import { FoundationSuperSystemType } from "building-types/foundations/foundationEnums";
import type { LevelDef } from "building-types/levels/LevelDef";
import { type Level } from "building-types/levels/levelTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type { Table, RWTable } from "common/data/table";
import { assertIsNotUndefined, assertIsNumber } from "common/utils/assertOfType";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { floorAssemblyFloorPlateThickness, makeLevelFloorAssembly } from "doc/elements/floor/floorAssembly";
import { foundationSystemSettingsFromSettingsConfig } from "doc/elements/foundation/foundationSettingsTools";
import { resolveOptionsInFormula, evalFormulas } from "doc/formula/evalFormulas";
import { DIST_EPSILON } from "geom/core";
import { StackedLevelDatumAbstraction } from "../elements/datums/datumAbstractions";
import {
  buildLevelDependencyGraph,
  buildLevelRelationshipGraph,
  detectLevelCircularDependencies,
  topologicalLevelSort,
} from "../elements/level/levelGenerationGraph";
import {
  getLevelDefDatumId,
  getLevelDefDatumOffset,
  groundLevel,
  isDatumLevelDef,
  isStackedLevelDef,
  levelDefHasStackAbove,
  worldKeyForLevel,
  groupByWorldKey,
} from "../elements/level/levelTools";
import { createLevelComponentInstances } from "../generationTools/components/componentLevels";
import {
  calcLevelFloorSettings,
  generateLevel,
  type LevelWithResolvedOffset,
  type LinkedLevelWithResolvedOffset,
} from "../generationTools/generateLevelsTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

// We assign a same floor index to levels that are within this tolerance of each other.
// A long chain of levels each within the tolerance of it's predecessor will have the same floor index.
const FLOOR_INDEX_TOLERANCE_INCHES = 6.0;

export function selectLevels({
  doc: {
    levels,
    optionSets,
    instanceOptionSets,
    settings: settingsTable,
    frameFloorAssemblies,
    slabFloorAssemblies,
    framingComponents,
    slabComponents,
    sheathingComponents,
  },
  optionChoices,
  settings,
  datumAbstractions,
  componentToTransforms,
  enabledFeatures,
  idToParents,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    levelDefs: levels,
    foundationSettings: settings.foundation,
    frameFloorAssemblies,
    slabFloorAssemblies,
    framingComponents,
    slabComponents,
    sheathingComponents,
    definitions,
    optionChoices: resolveOptionsInFormula({ levels }, definitions, optionChoices),
    levelsFloorSettings: calcLevelFloorSettings(settingsTable, levels, settings),
    datumAbstractions,
    componentToTransforms,
    enabledFeatures,
    idToParents,
  };
}

const compare = makeGenerationStageComparator<typeof selectLevels>({
  levelsFloorSettings: "deep",
  foundationSettings: "deep",
  optionChoices: "shallow",
  levelDefs: "deep",
  frameFloorAssemblies: "deep",
  slabFloorAssemblies: "deep",
  framingComponents: "deep",
  slabComponents: "deep",
  sheathingComponents: "deep",
  definitions: "deep",
  datumAbstractions: "shallow",
  componentToTransforms: "shallow",
  enabledFeatures: "shallow",
  idToParents: "shallow",
});

export function generateLevelsForWorlds(input: ReturnType<typeof selectLevels>): {
  levels: Building["levels"];
  groundLevel: Building["groundLevel"];
  issues: Building["issues"];
} {
  const { levelDefs, idToParents } = input;
  const levelDefsByWorldKey = groupByWorldKey(levelDefs, (id, _) => worldKeyForLevel(id, idToParents));

  let allLevels: RWTable<Level> = {};
  let allIssues: BuildingGenerationIssue[] = [];
  for (const worldKey in levelDefsByWorldKey) {
    const { levels, issues } = generateLevelsInternal({
      ...input,
      levelDefs: levelDefsByWorldKey[worldKey] ?? {},
    });
    allLevels = { ...allLevels, ...levels };
    allIssues = [...allIssues, ...issues];
  }

  return {
    levels: allLevels,
    groundLevel: groundLevel(allLevels),
    issues: allIssues,
  };
}

function generateLevelsInternal({
  levelDefs,
  optionChoices,
  foundationSettings,
  levelsFloorSettings,
  frameFloorAssemblies,
  slabFloorAssemblies,
  framingComponents,
  slabComponents,
  sheathingComponents,
  definitions,
  datumAbstractions,
  componentToTransforms,
  enabledFeatures,
}: ReturnType<typeof selectLevels>): {
  levels: Building["levels"];
  groundLevel: Building["groundLevel"];
  issues: Building["issues"];
} {
  const issues: BuildingGenerationIssue[] = [];
  const evaluatedLevelDefs: Table<LevelDef> = evalFormulas(levelDefs, (key) => optionChoices[key]);

  const foundationSystemSettings = foundationSystemSettingsFromSettingsConfig(foundationSettings);

  // helpers
  const getStackedAboveLevelId = (levelDef: LevelDef, lastPlacedLevelId: string | undefined): string | undefined => {
    if (levelDefHasStackAbove(levelDef)) {
      // Use the active dependency graph's target when present.
      const dependencies = activeDependencyGraph.dependencies[levelDef.id];
      if (dependencies && dependencies.size > 0) {
        return Array.from(dependencies)[0];
      }
      // Fallback to placement.stackAbove.
      return levelDef.placement.stackAbove;
    }

    return lastPlacedLevelId;
  };

  const getFloorPlateThickness = (levelDef: LevelDef): number => {
    const floorSettings = levelsFloorSettings[levelDef.id];
    const floorAssembly = makeLevelFloorAssembly({
      floorAssemblyId: floorSettings.floorAssembly,
      frameFloorAssemblies,
      slabFloorAssemblies,
      floorSettings,
      framingComponents,
      slabComponents,
      sheathingComponents,
    });

    assertIsNotUndefined(floorAssembly);

    return floorAssemblyFloorPlateThickness(framingComponents, slabComponents, sheathingComponents, floorAssembly);
  };

  // reset the last stacked level top of wall for the current generation
  StackedLevelDatumAbstraction.resetLastStackedLevelTopOfWall();

  // get active and valid level defs
  const allLevelDefsTable: RWTable<LevelDef> = {};
  const activeLevelDefsTable: RWTable<LevelDef> = {};
  for (const levelId in evaluatedLevelDefs) {
    const levelDef = evaluatedLevelDefs[levelId];

    const datumId = getLevelDefDatumId(levelDef, datumAbstractions);

    const datumAbstraction = datumAbstractions[datumId];

    // We allow no datum for level placement, but if there is a datum, it must be valid for level placement
    if (datumAbstraction && !datumAbstraction.isValidForUsage(DatumVerticalUsageType.LEVEL_PLACEMENT)) {
      issues.push({
        type: BuildingGenerationIssueType.INVALID_DATUM,
        handle: { type: "LEVEL", id: levelDef.id },
        message: `Level "${levelDef.name}" has an invalid datum "${datumId}"`,
      });

      continue;
    }

    allLevelDefsTable[levelId] = levelDef;

    if (levelDef.disabled) {
      continue;
    }

    activeLevelDefsTable[levelId] = levelDef;
  }

  // Build dependency graph for active levels only (used for generation order)
  // Pass both active levels and all evaluated levels to resolve through disabled levels
  const activeDependencyGraph = buildLevelDependencyGraph(activeLevelDefsTable, evaluatedLevelDefs);

  // Build relationship graph that includes all relationships (stackAbove, aboveLevelId, belowLevelId)
  // This is used for detecting contradictory level relationships, not for generation order
  const relationshipGraph = buildLevelRelationshipGraph(activeLevelDefsTable);

  // Detect circular dependencies in stacking (affects generation order)
  const stackingCircularDependencies = detectLevelCircularDependencies(activeDependencyGraph);
  for (const cycle of stackingCircularDependencies) {
    const cycleNames = cycle.map((id) => evaluatedLevelDefs[id]?.name ?? id).join(" → ");
    issues.push({
      type: BuildingGenerationIssueType.LEVEL_CIRCULAR_DEPENDENCY,
      handle: { type: "LEVEL", id: cycle[0] },
      message: `Circular dependency detected in level stacking: ${cycleNames}`,
    });
  }

  // Detect circular dependencies in level relationships (aboveLevelId/belowLevelId)
  const relationshipCircularDependencies = detectLevelCircularDependencies(relationshipGraph);
  for (const cycle of relationshipCircularDependencies) {
    // Skip if already reported from stacking dependencies
    const alreadyReported = stackingCircularDependencies.some(
      (stackCycle) => stackCycle[0] === cycle[0] && stackCycle.length === cycle.length,
    );
    if (!alreadyReported) {
      const cycleNames = cycle.map((id) => evaluatedLevelDefs[id]?.name ?? id).join(" → ");
      issues.push({
        type: BuildingGenerationIssueType.LEVEL_CIRCULAR_DEPENDENCY,
        handle: { type: "LEVEL", id: cycle[0] },
        message: `Contradictory level relationships detected: ${cycleNames}`,
      });
    }
  }

  // Perform topological sort to determine generation order
  const { order: activeOrder } = topologicalLevelSort(activeLevelDefsTable, activeDependencyGraph);
  const activeOrderSet = new Set(activeOrder);
  const allDependencyGraph = buildLevelDependencyGraph(allLevelDefsTable, evaluatedLevelDefs);
  const { order: allGenerationOrder } = topologicalLevelSort(allLevelDefsTable, allDependencyGraph);

  // Order all levels by dependency order
  const allFilteredLevelDefs = allGenerationOrder
    .map((id) => allLevelDefsTable[id])
    .filter((levelDef): levelDef is LevelDef => levelDef !== undefined);

  // legacy code for stacked basement level
  const stackedLevelDefs = allFilteredLevelDefs.filter((levelDef) => !levelDef.disabled && isStackedLevelDef(levelDef));
  const hasStackedBasement =
    foundationSystemSettings?.foundationSuperSystemType === FoundationSuperSystemType.BASEMENT &&
    stackedLevelDefs.length > 1;
  const stackedBasementLevelDef = hasStackedBasement ? stackedLevelDefs[0] : undefined;
  const stackedGroundLevelDef = hasStackedBasement ? stackedLevelDefs[1] : undefined;

  if (stackedGroundLevelDef && stackedBasementLevelDef) {
    const { topOfWall: basementTopOfWall } = stackedBasementLevelDef;
    const groundFloorPlateThickness = getFloorPlateThickness(stackedGroundLevelDef);

    assertIsNumber(basementTopOfWall);

    // set the last stacked level top of wall for the basement level generation
    StackedLevelDatumAbstraction.setLastStackedLevelTopOfWall(
      stackedBasementLevelDef.id,
      worldInchesNew(-groundFloorPlateThickness - basementTopOfWall),
    );
  }

  // Pre-compute auto-stacking references for active levels based on the active topological order.
  // This ensures active levels stack correctly regardless of the processing order in the single pass.
  const activeAutoStackAbove: Record<string, string | undefined> = {};
  {
    let tempLastActive: string | undefined = undefined;
    for (const id of activeOrder) {
      activeAutoStackAbove[id] = tempLastActive;
      tempLastActive = id;
    }
  }

  // Resolve offsets for all levels in a single pass.
  // Active levels use pre-computed stacking from the active topological order;
  // disabled levels use a running last-placed tracker so they slot into the stack naturally.
  let lastPlacedLevelId: string | undefined = undefined;

  function resolveOffsets(levelDefsToResolve: ReadonlyArray<LevelDef>): LevelWithResolvedOffset[] {
    return levelDefsToResolve.map((levelDef) => {
      const extraOffset = getLevelDefDatumOffset(levelDef);
      const isStackedLevel = isStackedLevelDef(levelDef);
      const datumId = getLevelDefDatumId(levelDef, datumAbstractions);
      const datumAbstraction = datumAbstractions[datumId];

      const lastPlacedForLevel = levelDef.disabled ? lastPlacedLevelId : activeAutoStackAbove[levelDef.id];
      const stackedAboveLevelId = getStackedAboveLevelId(levelDef, lastPlacedForLevel);
      const { id, topOfWall } = levelDef;
      const isBasementLevel = id === stackedBasementLevelDef?.id;
      const hasNonBasementLevelBelow =
        stackedAboveLevelId !== undefined && stackedAboveLevelId !== stackedBasementLevelDef?.id;
      const floorPlateThickness = getFloorPlateThickness(levelDef);
      const includesFloorPlateThickness = isStackedLevel && hasNonBasementLevelBelow;

      const resolvedOffset = datumAbstraction?.isValidForUsage(DatumVerticalUsageType.LEVEL_PLACEMENT)
        ? datumAbstraction.resolveVerticalDatum({
            currentLevelId: levelDef.id,
            stackedAboveLevelId,
            extraOffset: extraOffset + (includesFloorPlateThickness ? floorPlateThickness : 0),
          })
        : worldInchesNew(0);

      const isAboveGround = resolvedOffset > -DIST_EPSILON;

      if (isBasementLevel) {
        StackedLevelDatumAbstraction.setLastStackedLevelTopOfWall(id, worldInchesNew(0));
      } else {
        assertIsNumber(topOfWall);

        StackedLevelDatumAbstraction.setLastStackedLevelTopOfWall(
          id,
          worldInchesNew(resolvedOffset.valueOf() + topOfWall),
        );
      }

      lastPlacedLevelId = id;

      return {
        ...levelDef,
        isStackedLevel,
        resolvedOffset,
        isAboveGround,
        stackedAboveLevelId,
      };
    });
  }

  function computeLinkageAndFloorIndex(
    resolvedLevels: ReadonlyArray<LevelWithResolvedOffset>,
  ): LinkedLevelWithResolvedOffset[] {
    return resolvedLevels
      .map((levelDef, _index, levels) => {
        if (isDatumLevelDef(levelDef)) {
          return {
            ...levelDef,
            levelAbove: levelDef.placement.aboveLevelId,
            levelBelow: levelDef.placement.belowLevelId,
          };
        }
        return {
          ...levelDef,
          levelAbove: levels.find((other) => levelDef.id === other.stackedAboveLevelId)?.id,
          levelBelow: levelDef.stackedAboveLevelId,
        };
      })
      .sort((a, b) => a.resolvedOffset - b.resolvedOffset)
      .reduce((acc, levelDef) => {
        const lastLevel = acc[acc.length - 1];
        let floorIndex = 0;
        if (lastLevel) {
          const elevationDiff = levelDef.resolvedOffset - lastLevel.resolvedOffset;
          if (elevationDiff < FLOOR_INDEX_TOLERANCE_INCHES) {
            floorIndex = lastLevel.floorIndex;
          } else {
            floorIndex = lastLevel.floorIndex + 1;
          }
        }
        acc.push({ ...levelDef, floorIndex });
        return acc;
      }, [] as LinkedLevelWithResolvedOffset[]);
  }

  // Single resolution pass over all levels, then compute linkage separately:
  // active levels link among themselves, disabled levels link among all levels.
  const allWithOffsets = resolveOffsets(allFilteredLevelDefs);
  const resolvedLevelDefs = computeLinkageAndFloorIndex(allWithOffsets.filter((l) => activeOrderSet.has(l.id)));
  const allResolvedLevelDefs = computeLinkageAndFloorIndex(allWithOffsets);

  let levelsAboveGround = resolvedLevelDefs.filter((levelDef) => levelDef.isAboveGround);
  let levelsBelowGround = resolvedLevelDefs.filter((levelDef) => !levelDef.isAboveGround);
  let disabledLevelsAboveGround = allResolvedLevelDefs.filter(
    (levelDef) => levelDef.disabled && levelDef.isAboveGround,
  );
  let disabledLevelsBelowGround = allResolvedLevelDefs.filter(
    (levelDef) => levelDef.disabled && !levelDef.isAboveGround,
  );

  // Assign final floor index by shifting the temporary floor index to make levels below ground have negative floor index.
  if (levelsBelowGround.length > 0) {
    // Shift so the highest below ground level has floor index of -1.
    const floorIndexOffset = -levelsBelowGround[levelsBelowGround.length - 1].floorIndex - 1;
    levelsBelowGround = levelsBelowGround.map((levelDef) => ({
      ...levelDef,
      floorIndex: levelDef.floorIndex + floorIndexOffset,
    }));
    levelsAboveGround = levelsAboveGround.map((levelDef) => ({
      ...levelDef,
      floorIndex: levelDef.floorIndex + floorIndexOffset,
    }));
  }

  if (disabledLevelsBelowGround.length > 0) {
    const floorIndexOffset = -disabledLevelsBelowGround[disabledLevelsBelowGround.length - 1].floorIndex - 1;
    disabledLevelsBelowGround = disabledLevelsBelowGround.map((levelDef) => ({
      ...levelDef,
      floorIndex: levelDef.floorIndex + floorIndexOffset,
    }));
    disabledLevelsAboveGround = disabledLevelsAboveGround.map((levelDef) => ({
      ...levelDef,
      floorIndex: levelDef.floorIndex + floorIndexOffset,
    }));
  }

  const levels: RWTable<Level> = {};
  const temporaryDisabledLevelIds: string[] = [];

  // generate levels above ground upwards (reference level is the floor below)
  for (let i = 0; i < levelsAboveGround.length; i++) {
    const generatedLevel = generateLevel(
      levelsAboveGround[i],
      levels,
      levelsFloorSettings,
      frameFloorAssemblies,
      slabFloorAssemblies,
      framingComponents,
      slabComponents,
      sheathingComponents,
      foundationSystemSettings,
    );

    levels[generatedLevel.id] = generatedLevel;

    if (enabledFeatures.modelComponents) {
      createLevelComponentInstances(
        generatedLevel.id,
        levelDefs[generatedLevel.id],
        levelsAboveGround[i],
        generatedLevel,
        definitions,
        optionChoices,
        componentToTransforms,
        levels,
        foundationSystemSettings,
      );
    }
  }

  // generate levels below ground downwards (reference level is the floor above)
  for (let i = levelsBelowGround.length - 1; i >= 0; i--) {
    const generatedLevel = generateLevel(
      levelsBelowGround[i],
      levels,
      levelsFloorSettings,
      frameFloorAssemblies,
      slabFloorAssemblies,
      framingComponents,
      slabComponents,
      sheathingComponents,
      foundationSystemSettings,
    );

    levels[generatedLevel.id] = generatedLevel;

    if (enabledFeatures.modelComponents) {
      createLevelComponentInstances(
        generatedLevel.id,
        levelDefs[generatedLevel.id],
        levelsBelowGround[i],
        generatedLevel,
        definitions,
        optionChoices,
        componentToTransforms,
        levels,
        foundationSystemSettings,
      );
    }
  }

  if (!enabledFeatures.modelComponents) {
    return {
      levels,
      groundLevel: groundLevel(levels),
      issues,
    };
  }

  // Generate temporary archetype levels for globally disabled level defs so component instances
  // can still resolve and instantiate their per-instance level state.
  for (let i = 0; i < disabledLevelsAboveGround.length; i++) {
    const generatedLevel = generateLevel(
      disabledLevelsAboveGround[i],
      levels,
      levelsFloorSettings,
      frameFloorAssemblies,
      slabFloorAssemblies,
      framingComponents,
      slabComponents,
      sheathingComponents,
      foundationSystemSettings,
    );

    levels[generatedLevel.id] = generatedLevel;
    temporaryDisabledLevelIds.push(generatedLevel.id);

    createLevelComponentInstances(
      generatedLevel.id,
      levelDefs[generatedLevel.id],
      disabledLevelsAboveGround[i],
      generatedLevel,
      definitions,
      optionChoices,
      componentToTransforms,
      levels,
      foundationSystemSettings,
    );
  }

  for (let i = disabledLevelsBelowGround.length - 1; i >= 0; i--) {
    const generatedLevel = generateLevel(
      disabledLevelsBelowGround[i],
      levels,
      levelsFloorSettings,
      frameFloorAssemblies,
      slabFloorAssemblies,
      framingComponents,
      slabComponents,
      sheathingComponents,
      foundationSystemSettings,
    );

    levels[generatedLevel.id] = generatedLevel;
    temporaryDisabledLevelIds.push(generatedLevel.id);

    createLevelComponentInstances(
      generatedLevel.id,
      levelDefs[generatedLevel.id],
      disabledLevelsBelowGround[i],
      generatedLevel,
      definitions,
      optionChoices,
      componentToTransforms,
      levels,
      foundationSystemSettings,
    );
  }

  for (const levelId of temporaryDisabledLevelIds) {
    delete levels[levelId];
  }

  return {
    levels,
    groundLevel: groundLevel(levels),
    issues,
  };
}

export default memo(selectLevels, generateLevelsForWorlds, compare);
